"use client";

import { useMemo, useState } from "react";
import { useUser, useSignIn, useSignUp } from "@clerk/clerk-react";
import { ExpandableChatWidget } from "./ExpandableChatWidget";
import { ClerkWrapper } from "./ClerkWrapper";
import { SSRWrapper } from "./SSRWrapper";
import { ZYNEXTECH_CLERK_PUBLISHABLE_KEY } from "../config/clerk";

export interface ExpandableChatWidgetWrapperProps {
  endpoint?: string;
  placeholder?: string;
  className?: string;
  organizationId?: string;
  publicScope?: boolean;
  allowAllAccess?: boolean;
  // Branding props
  logoUrl?: string;
  logoAlt?: string;
  brandName?: string;
  // Widget styling
  bubbleClassName?: string;
  widgetClassName?: string;
}

// Internal component that uses Clerk hooks
function ExpandableChatWidgetInternal(props: Omit<ExpandableChatWidgetWrapperProps, 'clerkPublishableKey'>) {
  const { isSignedIn, user, isLoaded: userLoaded } = useUser();
  const { signIn, isLoaded: signInLoaded, setActive: setSignInActive } = useSignIn();
  const { signUp, isLoaded: signUpLoaded, setActive: setSignUpActive } = useSignUp();
  
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Memoize auth state to prevent unnecessary re-renders
  const authState = useMemo(() => ({
    isSignedIn: !!isSignedIn,
    user: user,
    userLoaded: !!userLoaded
  }), [isSignedIn, user?.id, userLoaded]); // Only track user ID to avoid deep comparison

  // Stable auth handlers
  const handleSignIn = useMemo(() => async (email: string, password: string) => {
    if (!signInLoaded) throw new Error("Sign in not ready");
    
    setAuthError(null);
    setAuthLoading(true);

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === "complete") {
        await setSignInActive({ session: result.createdSessionId });
      } else {
        throw new Error("Sign in incomplete");
      }
    } catch (err: any) {
      const errorMessage = err?.errors?.[0]?.message || "Failed to sign in";
      setAuthError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  }, [signIn, signInLoaded, setSignInActive]);

  const handleSignUp = useMemo(() => async (email: string, password: string, fullName: string) => {
    if (!signUpLoaded) throw new Error("Sign up not ready");
    
    setAuthError(null);
    setAuthLoading(true);

    try {
      const result = await signUp.create({
        emailAddress: email,
        password,
        firstName: fullName.split(' ')[0],
        lastName: fullName.includes(' ') ? fullName.split(' ').slice(1).join(' ') : '',
      });

      if (result.status === 'complete') {
        await setSignUpActive({ session: result.createdSessionId });
      } else {
        setAuthError("Please check your email to complete signup.");
        throw new Error("Please check your email to complete signup.");
      }
    } catch (err: any) {
      const errorMessage = err?.errors?.[0]?.message || "Failed to create account";
      setAuthError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  }, [signUp, signUpLoaded, setSignUpActive]);

  return (
    <ExpandableChatWidget
      {...props}
      isSignedIn={authState.isSignedIn}
      user={authState.user}
      userLoaded={authState.userLoaded}
      onSignIn={handleSignIn}
      onSignUp={handleSignUp}
      authLoading={authLoading}
      authError={authError}
      // Pass through branding props
      logoUrl={props.logoUrl}
      logoAlt={props.logoAlt}
      brandName={props.brandName}
      // Pass through styling props
      className={props.bubbleClassName || props.className}
      widgetClassName={props.widgetClassName}
    />
  );
}

// Main wrapper component that provides Clerk context with SSR protection
export function ExpandableChatWidgetWrapper(props: ExpandableChatWidgetWrapperProps) {
  return (
    <SSRWrapper fallback={<div style={{ display: 'none' }} />}>
      <ClerkWrapper publishableKey={ZYNEXTECH_CLERK_PUBLISHABLE_KEY}>
        <ExpandableChatWidgetInternal {...props} />
      </ClerkWrapper>
    </SSRWrapper>
  );
} 